import { translate as t } from '../lib/I18n';
import { ActionTypes, GlobalModals, TimeDirections, AccountPages, DisruptionAlertMaximumAlerts } from '../constants/AppConstants';
import AppDispatcher from '../dispatcher/AppDispatcher';
import { InvalidSearchError } from '../errors/Search';
import { filterSameDayDeparturesOnly } from '../helpers/DisruptionAlert';
import DisruptionAlertMute from '../records/DisruptionAlertMute';
import DisruptionAlertService from '../services/DisruptionAlertService';
import JourneyPlanService from '../services/JourneyPlanService';
import { DisruptionAlertsLimitExceededError } from '../errors/DisruptionAlerts';
import { getBrandInfo } from '../lib/brand';
import urls from '../urls';


const DisruptionAlertActions = {

    async loadAlerts (user) {
        AppDispatcher.dispatch({ actionType: ActionTypes.DISRUPTION_ALERTS_LOAD_STARTED });

        try {
            const alerts = await DisruptionAlertService.getAll(user);
            AppDispatcher.dispatch({
                actionType: ActionTypes.DISRUPTION_ALERTS_LOAD_FINISHED,
                alerts,
            });
        } catch (error) {
            AppDispatcher.dispatch({ actionType: ActionTypes.DISRUPTION_ALERTS_LOAD_FAILED });
            throw error;
        }
    },

    async loadServiceChangeAlerts (user) {
        AppDispatcher.dispatch({ actionType: ActionTypes.SERVICE_CHANGE_ALERTS_LOAD_STARTED });

        try {
            const timetableAlerts = await DisruptionAlertService.getServiceChangeAlerts(user);
            AppDispatcher.dispatch({
                actionType: ActionTypes.SERVICE_CHANGE_ALERTS_LOAD_FINISHED,
                timetableAlerts,
            });
        } catch (error) {
            AppDispatcher.dispatch({ actionType: ActionTypes.SERVICE_CHANGE_ALERTS_LOAD_FAILED });
            throw error;
        }
    },

    async createFromOrderJourney ({ user, order, orderTrip, orderJourney, context, stationFacilityAlert, isEmailNotification, isCloudMessageNotification }) {
        let alert = null;
        try {
            alert = await DisruptionAlertService.createFromOrderJourney({ user, order, orderTrip, orderJourney, stationFacilityAlert, isEmailNotification, isCloudMessageNotification });
            AppDispatcher.dispatch({
                actionType: ActionTypes.DISRUPTION_ALERT_CREATED,
                alert,
                context
            });
        } catch (err) {
            if (err instanceof DisruptionAlertsLimitExceededError) {
                const data = {
                    heading: t('account.disruptionAlerts.unableToCreate'),
                    title: t('account.disruptionAlerts.limitReached'),
                    message: t('account.disruptionAlerts.create.maximumReached', { max: DisruptionAlertMaximumAlerts })
                };

                if (getBrandInfo().isTranspennine) {
                    data.customButton = {
                        text: 'account.disruptionAlerts.manageAlerts',
                        onClick () {
                            this.push(urls.location(AccountPages.ACCOUNT_DISRUPTION_ALERTS));
                        }
                    };
                }

                AppDispatcher.dispatch({
                    actionType: ActionTypes.MODAL_OPENED,
                    type: GlobalModals.ERROR,
                    data
                });
            } else {
                AppDispatcher.dispatch({
                    actionType: ActionTypes.MODAL_OPENED,
                    type: GlobalModals.ERROR,
                    data: {
                        heading: t('account.disruptionAlerts.unableToCreate'),
                        message: t('account.disruptionAlerts.genericError')
                    }
                });
            }

        }
    },

    async createFromBuilder ({ user, order, orderTrip, formData, context, stationFacilityAlert }) {
        const alert = await DisruptionAlertService.createFromBuilder({ user, order, orderTrip, formData, stationFacilityAlert });
        AppDispatcher.dispatch({
            actionType: ActionTypes.DISRUPTION_ALERT_CREATED,
            alert,
            context,
        });
        return alert;
    },

    async update (user, alertData, context) {
        const alert = await DisruptionAlertService.update(user, alertData);
        AppDispatcher.dispatch({
            actionType: ActionTypes.DISRUPTION_ALERT_UPDATED,
            alert,
            context,
        });
    },

    async delete (user, alert, context) {
        await DisruptionAlertService.delete(user, alert);
        AppDispatcher.dispatch({
            actionType: ActionTypes.DISRUPTION_ALERT_DELETED,
            alert,
            context,
        });
    },

    async createMute (user, alert, muteData, context) {
        const mute = new DisruptionAlertMute(muteData);
        const alertMute = await DisruptionAlertService.createMute(user, alert, mute);
        AppDispatcher.dispatch({
            actionType: ActionTypes.DISRUPTION_ALERT_MUTE_CREATED,
            alert,
            alertMute,
            context,
        });
    },

    async editMute (user, alert, alertMute, context) {
        const updatedAlertMute = await DisruptionAlertService.editMute(user, alert, alertMute);
        AppDispatcher.dispatch({
            actionType: ActionTypes.DISRUPTION_ALERT_MUTE_UPDATED,
            alert,
            alertMute: updatedAlertMute,
            context,
        });
    },

    async deleteMute (user, alert, alertMute, context) {
        await DisruptionAlertService.deleteMute(user, alert, alertMute);
        AppDispatcher.dispatch({
            actionType: ActionTypes.DISRUPTION_ALERT_MUTE_DELETED,
            alert,
            alertMute,
            context,
        });
    },

    async muteForDay (ddmUserId, alertId, token, muteAll) {
        await DisruptionAlertService.muteForDay(ddmUserId, alertId, token, muteAll);
        AppDispatcher.dispatch({
            actionType: ActionTypes.DISRUPTION_ALERT_MUTED_FOR_DAY,
            alertId,
            muteAll,
        });
    },

    async serviceSearch (user, search) {
        if (!search.isValid()) {
            const errors = search.getValidationErrors().toJS().join(', ');

            AppDispatcher.dispatch({
                actionType: ActionTypes.DISRUPTION_ALERTS_SEARCH_FAILED,
                search,
            });

            throw new InvalidSearchError(`Failed check: ${errors}`, search);
        }

        AppDispatcher.dispatch({
            actionType: ActionTypes.DISRUPTION_ALERTS_SEARCH_STARTED,
            search,
        });

        const result = await JourneyPlanService.search({ search, user });
        const sameDayServices = filterSameDayDeparturesOnly(result.outward, search.getOutboundDateTime());

        AppDispatcher.dispatch({
            actionType: ActionTypes.DISRUPTION_ALERTS_SEARCH_FINISHED,
            search,
            servicesList: sameDayServices,
        });

        return { search, result };
    },

    async showEarlierLaterServices (user, search, tripDirection, services, timeDirection) {
        if (!Object.values(TimeDirections).includes(timeDirection)) {
            return null;
        }

        const isEarlier = timeDirection === TimeDirections.EARLIER;
        const actionTypePrefix = `DISRUPTION_ALERTS_SEARCH_${isEarlier ? 'EARLIER' : 'LATER'}`;
        const updatedSearch = isEarlier
            ? search.earlier(tripDirection, services)
            : search.later(tripDirection, services);

        AppDispatcher.dispatch({
            actionType: ActionTypes[`${actionTypePrefix}_STARTED`],
            search: updatedSearch,
        });

        const result = await JourneyPlanService.search({ search: updatedSearch, user });
        const sameDayServices = filterSameDayDeparturesOnly(result.outward, search.getOutboundDateTime());

        AppDispatcher.dispatch({
            actionType: ActionTypes[`${actionTypePrefix}_FINISHED`],
            search: updatedSearch,
            servicesList: sameDayServices,
        });

        return { updatedSearch, result };
    },

    async clearServices() {
        AppDispatcher.dispatch({ actionType: ActionTypes.DISRUPTION_ALERTS_SERVICES_RESET });
    },

    async getBankHolidays() {
        AppDispatcher.dispatch({ actionType: ActionTypes.DISRUPTION_ALERTS_FETCH_BANK_HOLIDAYS_STARTED });

        try {
            const dates = await DisruptionAlertService.getBankHolidays();
            AppDispatcher.dispatch({
                actionType: ActionTypes.DISRUPTION_ALERTS_FETCH_BANK_HOLIDAYS_FINISHED,
                dates,
            });
        } catch (error) {
            AppDispatcher.dispatch({ actionType: ActionTypes.DISRUPTION_ALERTS_FETCH_BANK_HOLIDAYS_FAILED });
            throw error;
        }
    },

    setTimetableAlertsWidgetState (formState) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.SET_TIMETABLE_ALERTS_WIDGET_STATE,
            formState,
        });
    },

    async createTimetableChangesAlert ({ timetableChangesAlert }) {
        await DisruptionAlertService.createServiceChangeAlert({ timetableChangesAlert });
    },

    async deleteServiceChangeAlert (user, alrt) {
        await DisruptionAlertService.deleteServiceChangeAlert(user, alrt);
        AppDispatcher.dispatch({
            actionType: ActionTypes.SERVICE_CHANGE_ALERT_DELETED,
            alrt,
        });
    },

    async deleteServiceChangeAlertAnon ({ alertId, ddmCustomerId, orderId, securityToken, ticketIds }) {
        await DisruptionAlertService.deleteServiceChangeAlertAnon({ alertId, ddmCustomerId, orderId, securityToken, ticketIds });
    },

    addTimetableAlert () {
        AppDispatcher.dispatch({
            actionType: ActionTypes.ADD_TIMETABLE_ALERT
        });
    },

    resetTimetableAlerts () {
        AppDispatcher.dispatch({
            actionType: ActionTypes.RESET_TIMETABLE_ALERTS
        });
    }
};

export default DisruptionAlertActions;
